/**
 * Checks if a string is in base64 format, returning boolean
 *
 * @param {string} string String to test
 */
export const isBase64 = (string) => {
  const regex =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return regex.test(string)
}

import { LitElement, html, css, unsafeCSS } from '@hh/global.js'

class AdaptiveImage extends LitElement {
  static properties = {
    visibleOn: {
      type: String,
      attribute: 'visible-on',
    },
    isMobile: {
      type: Boolean,
      state: true
    }
  }

  constructor() {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [493, 600, 713, 823, 1100, 1206, 1346, 1426, 1646, 1946]

    const imageData = isBase64(this.dataset.image)
      ? atob(this.dataset.image)
      : this.dataset.image
    try {
      this.image = JSON.parse(imageData)
    } catch (e) {
      this.image = imageData
    }

    const mobileImageData = isBase64(this.dataset.mobileImage)
      ? atob(this.dataset.mobileImage)
      : this.dataset.mobileImage
    try {
      this.mobileImage = JSON.parse(mobileImageData)
    } catch (e) {
      this.mobileImage = mobileImageData
    }

    this.width = this.image.width || 1946
    this.mobileWidth = this.mobileImage.width || 750
    this.src = this.image?.src || this.image
    this.mobileSrc = this.mobileImage?.src || this.mobileImage
    this.alt = this.dataset.alt || this.image?.alt || ''
    this.sizes = this.dataset.sizes || '100vw'
    this.loading = this.dataset.loading || 'lazy'
    this.objectFit = this.dataset.objectFit || 'cover'
    this.objectPosition = this.dataset.objectPosition || 'center'
    this.aspectRatio =
      parseFloat(this.dataset.aspectRatio) || 1
    this.mobileAspectRatio = parseFloat(this.dataset.mobileAspectRatio) || this.aspectRatio || 1

    this.style.setProperty('--hh-object-fit', this.objectFit)
    this.style.setProperty('--hh-object-position', this.objectPosition)
    this.style.setProperty('--hh-aspect-ratio', this.aspectRatio)
    this.style.setProperty('--hh-mobile-aspect-ratio', this.mobileAspectRatio)

    this.attachShadow({ mode: 'open' })
    this.render()
  }

  connectedCallback() {
    super.connectedCallback()

    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    this._handleViewportChange = this._handleViewportChange.bind(this);
    this._mobileQuery.addEventListener('change', this._handleViewportChange);
    this._handleViewportChange(this._mobileQuery);
  }

  disconnectedCallback() {
    this._mobileQuery.removeEventListener('change', this._handleViewportChange);
  }

  _handleViewportChange(e) {
    this.isMobile = e.matches;
  }

  updateImage(data) {
    Object.assign(this, data)
    this.render()
  }

  generateSrcset(src, imgWidth) {
    let srcset = ''

    for (const width of this.srcsetWidths) {
      if (imgWidth >= width) srcset += `${src}&width=${width} ${width}w,`
    }
    srcset += `${src} ${imgWidth}w`
    return srcset
  }

  render() {
    const visibility = this.visibleOn || 'all'
    const shouldRender =
      visibility === 'all' ||
      (visibility === 'mobile' && this.isMobile) ||
      (visibility === 'desktop' && !this.isMobile)

    if (!shouldRender) return html``
    
    return html`
      <picture>
        ${this.mobileImage
          ? html`<source
                media="(min-width: 750px)"
                srcset="${this.generateSrcset(this.src, this.width)}"
                sizes="${this.sizes}"
              />
              <source
                media="(max-width: 749px)"
                srcset="
                  ${this.generateSrcset(this.mobileSrc, this.mobileWidth)}
                "
                sizes="${this.sizes}"
              />`
          : html`<source srcset="${this.generateSrcset(
              this.src,
              this.width
            )}" sizes="${this.sizes}"></source>`}

        <img
          id="image-element"
          src="${this.src}&width=${this.srcsetWidths[0]}"
          alt="${this.alt}"
          loading="${this.loading}"
        />
      </picture>
    `
  }

  static styles = css`
    :host {
      display: block;
      height: auto;
      position: relative;
      width: 100%;
      aspect-ratio: var(--hh-mobile-aspect-ratio);
    }
    @media screen and (min-width: 750px) {
      :host {
        aspect-ratio: var(--hh-aspect-ratio);
      }
    }
    @supports not (aspect-ratio: ${unsafeCSS('var(--hh-aspect-ratio)')}) {
      :host::before {
        float: left;
        padding-top: calc(100% / ${unsafeCSS('var(--hh-aspect-ratio)')});
        content: '';
      }
      :host::after {
        display: block;
        clear: both;
        content: '';
      }
    }
    #image-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit);
      object-position: var(--hh-object-position);
      -webkit-transform: translate3d(0, 0, 0);
    }
  `
}

customElements.define('adaptive-image', AdaptiveImage)
